import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import { terminal } from '../app/terminal.js';
import { useApp } from '@weke/ui';
import { statementCsv, totals } from '@weke/ui';
import {
  formatMoney,
  formatPrice,
  formatSignedMoney,
  formatTimestamp,
  formatVolume,
  priceDigits,
} from '@weke/ui';
import { Panel } from '@weke/ui';

type Period = 'all' | 'today' | '7d' | '30d';

const PERIOD_LABEL: Record<Period, string> = {
  all: 'All',
  today: 'Today',
  '7d': '7 days',
  '30d': '30 days',
};

/**
 * Deals and the running balance — the trader's statement view.
 *
 * The running balance is the exact fold of the server's own per-deal
 * `balance_delta_minor` (see `lib/csv.ts`), anchored on the account's current
 * balance, so the last row always equals the balance in the header. Filtering by
 * period hides rows; it never restarts the balance, because a statement that
 * silently re-bases its own running total is worse than no statement.
 *
 * The export is RFC 4180 CSV with the numbers as canonical strings, so a
 * spreadsheet reads them exactly. P8 owns the official, server-generated
 * statement; this one says so in its header line.
 */
export function HistoryPanel(): ReactElement {
  const app = useApp();
  const [period, setPeriod] = useState<Period>('all');
  const digits = app.account?.digits ?? 2;
  const currency = app.account?.currency ?? 'USD';
  /** Deals carry the symbol name; the catalogue carries its display digits. */
  const symbolDigits = useMemo(() => {
    const map: Record<string, number> = {};
    for (const symbol of app.symbols) {
      map[symbol.name] = priceDigits(symbol.digits, symbol.tick_size_e8);
    }
    return map;
  }, [app.symbols]);

  const cutoff = useMemo<string | null>(() => {    if (period === 'all') return null;
    const now = Date.now() + app.serverOffsetMs;
    const days = period === 'today' ? 0 : period === '7d' ? 7 : 30;
    if (period === 'today') return new Date(now).toISOString().slice(0, 10);
    return new Date(now - days * 86_400_000).toISOString();
  }, [app.serverOffsetMs, period]);

  const rows = useMemo(
    () =>
      app.deals.filter((deal) =>
        cutoff === null ? true : period === 'today' ? deal.created_at.slice(0, 10) === cutoff : deal.created_at >= cutoff,
      ),
    [app.deals, cutoff, period],
  );
  const sums = useMemo(() => totals(rows), [rows]);

  const exportCsv = (): void => {
    const csv = statementCsv(app.deals, {
      currency,
      digits,
      accountId: app.session?.accountId ?? '',
      generatedAt: new Date().toISOString(),
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `weke-statement-${(app.session?.accountId ?? 'account').slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Panel
      id="history"
      title="History"
      badge={`${rows.length} deals · balance ${formatMoney(app.account?.balance_minor ?? null, digits)} ${currency}`}
      actions={
        <span className="flex items-center gap-1">
          {(Object.keys(PERIOD_LABEL) as Period[]).map((entry) => (
            <button
              key={entry}
              type="button"
              className={period === entry ? 'chip chip-on cursor-pointer' : 'chip cursor-pointer'}
              aria-pressed={period === entry}
              onClick={() => setPeriod(entry)}
            >
              {PERIOD_LABEL[entry]}
            </button>
          ))}
          <button
            type="button"
            className="btn h-5"
            onClick={() => void terminal.refreshDeals()}
            title="Reload deals from the history service"
          >
            {app.dealsLoading ? '…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="btn h-5"
            onClick={exportCsv}
            disabled={app.deals.length === 0}
            title="Download the client-side CSV view (not the official statement)"
            data-testid="export-csv"
          >
            CSV
          </button>
          <button
            type="button"
            className="btn h-5"
            onClick={() => void terminal.downloadStatement('pdf')}
            disabled={app.deals.length === 0}
            title="Download the official server-generated statement (PDF): a pure fold of the journal, verified and byte-reproducible"
            data-testid="download-statement"
          >
            Statement
          </button>
        </span>
      }
    >
      <table className="data-table" aria-label="Deal history">
        <thead>
          <tr>
            <th>Time</th>
            <th>Symbol</th>
            <th>Type</th>
            <th>Side</th>
            <th>Volume</th>
            <th>Price</th>
            <th>P&amp;L</th>
            <th>Swap</th>
            <th>Comm.</th>
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={10} className="muted py-3 text-center">
                {app.deals.length === 0
                  ? 'No deals yet. The first fill will appear here.'
                  : 'No deals in this period.'}
              </td>
            </tr>
          ) : null}
          {rows.map((deal) => {
            const realised = deal.realised_pnl_minor;
            const negative = realised.startsWith('-');
            return (
              <tr key={deal.deal_id} data-testid={`deal-${deal.deal_id}`}>
                <td className="num muted">{formatTimestamp(deal.created_at)}</td>
                <td>{deal.symbol}</td>
                <td className="faint">{deal.deal_type.toLowerCase()}</td>
                <td className={deal.side === 'BUY' ? 'buy' : 'sell'}>{deal.side}</td>
                <td className="num">{formatVolume(deal.volume_e4, '100')}</td>
                <td className="num">{formatPrice(deal.price_e8, symbolDigits[deal.symbol] ?? 5)}</td>
                <td
                  className={`num ${negative ? 'loss' : realised === '0' ? 'faint' : 'profit'}`}
                  data-testid="deal-pnl"
                >
                  {realised === '0' ? '—' : formatSignedMoney(realised, digits)}
                </td>
                <td className="num faint">{formatMoney(deal.swap_minor, digits)}</td>
                <td className="num faint">{formatMoney(deal.commission_minor, digits)}</td>
                <td className="num" data-testid="deal-balance">
                  {formatMoney(deal.balance_after_minor, digits)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-line-strong">
            <td colSpan={6} className="muted px-2 py-1 text-2xs uppercase tracking-wider">
              Totals for the period
            </td>
            <td className={`num text-right ${sums.realisedMinor.startsWith('-') ? 'loss' : ''}`}>
              {formatSignedMoney(sums.realisedMinor, digits)}
            </td>
            <td className="num text-right faint">{formatMoney(sums.swapMinor, digits)}</td>
            <td className="num text-right faint">{formatMoney(sums.commissionMinor, digits)}</td>
            <td className="num text-right">{formatMoney(app.account?.balance_minor ?? null, digits)}</td>
          </tr>
        </tfoot>
      </table>
    </Panel>
  );
}
